/**
 * Job State Machine Service
 *
 * Phase 3 - Core state machine that drives the entire tech job UX.
 *
 * State Flow:
 *   SCHEDULED → EN_ROUTE → ON_SITE → WORKING → WRAPPING_UP → COMPLETED
 *                                                     │
 *                                                     ↓
 *                                               NEEDS_RETURN_VISIT
 *
 * Each transition:
 * - Validates prerequisites
 * - Sets timestamps
 * - Sets Zapier lastAction signal
 * - Updates Firestore
 */

import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { Job, JobState } from '@/types/schema';
import { enqueueAction, isOffline, type OfflineAction } from '@/services/offline-queue.service';
import { executeJobSideEffects, type SideEffectResult } from '@/services/job-side-effects.service';

// ============================================================================
// VALID TRANSITIONS
// ============================================================================

/**
 * Map of valid transitions: from -> to[]
 */
const VALID_TRANSITIONS: Record<JobState, JobState[]> = {
  SCHEDULED: ['EN_ROUTE'],
  EN_ROUTE: ['ON_SITE', 'WORKING', 'SCHEDULED'], // Can cancel en route back to scheduled; WORKING = skip ON_SITE (no JSA)
  ON_SITE: ['WORKING'], // Legacy: kept for backward compat
  WORKING: ['COMPLETED', 'NEEDS_RETURN_VISIT', 'PAUSED'], // Direct completion — no WRAPPING_UP step
  PAUSED: ['WORKING'],
  WRAPPING_UP: ['COMPLETED', 'NEEDS_RETURN_VISIT'], // Legacy: kept for jobs already in this state
  COMPLETED: [], // Terminal state
  NEEDS_RETURN_VISIT: [], // Terminal state (creates follow-up job)
};

/**
 * Reverse ("undo") transitions — let a tech step BACK a state when they
 * tapped a forward action by mistake (e.g. "On my way" / "I've arrived"
 * before they actually had). Maps the current state to the state it undoes to.
 *
 * Only the pre-completion movement states are reversible here. Terminal states
 * (COMPLETED / NEEDS_RETURN_VISIT) are intentionally NOT reversible — undoing a
 * completion has heavy downstream side effects (deal advance, service
 * activation, AI summary, follow-up job creation) and must be handled by the
 * office, not silently rolled back from the tech's phone.
 *
 * Note: WORKING normally undoes to EN_ROUTE (keeping the travel leg), but falls
 * back to SCHEDULED when there was no en-route leg — see getRevertInfo.
 */
const REVERT_TRANSITIONS: Partial<Record<JobState, JobState>> = {
  EN_ROUTE: 'SCHEDULED',
  ON_SITE: 'EN_ROUTE',
  WORKING: 'EN_ROUTE',
};

/**
 * Zapier lastAction signals for each transition
 */
const TRANSITION_SIGNALS: Record<string, string> = {
  'SCHEDULED->EN_ROUTE': 'tech_en_route',
  'EN_ROUTE->ON_SITE': 'tech_arrived',
  'EN_ROUTE->WORKING': 'tech_arrived_working', // Skip ON_SITE, go straight to work
  'EN_ROUTE->SCHEDULED': 'tech_cancelled_route',
  'ON_SITE->WORKING': 'tech_started_work',
  'WORKING->COMPLETED': 'job_completed',
  'WORKING->NEEDS_RETURN_VISIT': 'return_visit_requested',
  'WORKING->PAUSED': 'tech_paused',
  'PAUSED->WORKING': 'tech_resumed',
  'WRAPPING_UP->COMPLETED': 'job_completed', // Legacy
  'WRAPPING_UP->NEEDS_RETURN_VISIT': 'return_visit_requested', // Legacy
};

// ============================================================================
// STATE INFO
// ============================================================================

export interface StateInfo {
  label: string;
  color: string;
  bgClass: string;
  textClass: string;
  animate?: boolean;
}

export const JOB_STATE_INFO: Record<JobState, StateInfo> = {
  SCHEDULED: {
    label: 'Scheduled',
    color: 'slate',
    bgClass: 'bg-slate-100',
    textClass: 'text-slate-600',
  },
  EN_ROUTE: {
    label: 'En Route',
    color: 'sky',
    bgClass: 'bg-sky-100',
    textClass: 'text-sky-700',
    animate: true,
  },
  ON_SITE: {
    label: 'On Site',
    color: 'amber',
    bgClass: 'bg-amber-100',
    textClass: 'text-amber-700',
  },
  WORKING: {
    // Visually same as On Site — tech is on site and working
    label: 'On Site',
    color: 'amber',
    bgClass: 'bg-amber-100',
    textClass: 'text-amber-700',
  },
  PAUSED: {
    // Visually same as On Site — tech is still on site, just paused
    label: 'On Site',
    color: 'amber',
    bgClass: 'bg-amber-100',
    textClass: 'text-amber-700',
  },
  WRAPPING_UP: {
    label: 'Wrapping Up',
    color: 'indigo',
    bgClass: 'bg-indigo-100',
    textClass: 'text-indigo-700',
  },
  COMPLETED: {
    label: 'Completed',
    color: 'slate',
    bgClass: 'bg-slate-100',
    textClass: 'text-slate-600',
  },
  NEEDS_RETURN_VISIT: {
    label: 'Return Required',
    color: 'orange',
    bgClass: 'bg-orange-100',
    textClass: 'text-orange-700',
  },
};

// ============================================================================
// TRANSITION ACTIONS
// ============================================================================

export interface TransitionAction {
  label: string;
  toState: JobState;
  icon: 'car' | 'navigation' | 'map-pin' | 'play' | 'pause' | 'check' | 'rotate-ccw' | 'x';
  variant: 'primary' | 'success' | 'warning' | 'danger' | 'secondary';
}

/**
 * Get available actions for the current job state.
 * Returns the contextual action buttons to render.
 */
export function getActionsForState(job: Job): TransitionAction[] {
  const state = resolveJobState(job);
  const actions: TransitionAction[] = [];

  switch (state) {
    case 'SCHEDULED':
      actions.push({
        label: 'On My Way',
        toState: 'EN_ROUTE',
        icon: 'car',
        variant: 'primary',
      });
      break;

    case 'EN_ROUTE':
      // Skip ON_SITE entirely — arrival starts work immediately
      actions.push({
        label: 'I\'ve Arrived',
        toState: 'WORKING',
        icon: 'map-pin',
        variant: 'success',
      });
      break;

    case 'ON_SITE':
      // Legacy: if a job is stuck in ON_SITE, allow starting work
      actions.push({
        label: 'Start Work',
        toState: 'WORKING',
        icon: 'play',
        variant: 'success',
      });
      break;

    case 'WORKING':
      actions.push({
        label: 'Complete Job',
        toState: 'COMPLETED',
        icon: 'check',
        variant: 'success',
      });
      actions.push({
        label: 'Pause',
        toState: 'PAUSED',
        icon: 'pause',
        variant: 'secondary',
      });
      break;

    case 'PAUSED':
      actions.push({
        label: 'Resume',
        toState: 'WORKING',
        icon: 'play',
        variant: 'success',
      });
      break;

    case 'WRAPPING_UP':
      // Legacy: jobs already in this state can still complete
      actions.push({
        label: 'Complete Job',
        toState: 'COMPLETED',
        icon: 'check',
        variant: 'success',
      });
      break;

    // COMPLETED and NEEDS_RETURN_VISIT are terminal - no actions
  }

  return actions;
}

// ============================================================================
// STATE RESOLUTION
// ============================================================================

/**
 * Resolve the current job state.
 * Uses the new jobState field, or maps from legacy status fields.
 */
export function resolveJobState(job: Job): JobState {
  // If jobState is explicitly set, use it
  if (job.jobState) return job.jobState;

  // Map from legacy status
  if (job.status === 'Completed') return 'COMPLETED';
  if (job.status === 'In Progress') {
    // Determine sub-state from timestamps
    if (job.onsiteStartedAt && !job.onsiteEndedAt) return 'WORKING';
    if (job.onsiteEndedAt) return 'WRAPPING_UP';
    return 'WORKING';
  }

  return 'SCHEDULED';
}

// ============================================================================
// TRANSITION LOGIC
// ============================================================================

/**
 * Check if a state transition is valid
 */
export function canTransition(fromState: JobState, toState: JobState): boolean {
  return VALID_TRANSITIONS[fromState]?.includes(toState) ?? false;
}

/**
 * Compute on-site minutes excluding paused time.
 * If completing/wrapping up while still paused, the in-flight pause segment is also subtracted.
 */
export function computeActualDurationMinutes(job: Job, endIso: string): number {
  if (!job.onsiteStartedAt) return 0;
  const startMs = new Date(job.onsiteStartedAt).getTime();
  const endMs = new Date(endIso).getTime();
  const accumulatedPausedMs = job.pausedTotalMs || 0;
  const inFlightPausedMs = job.pausedAt ? Math.max(0, endMs - new Date(job.pausedAt).getTime()) : 0;
  const workingMs = Math.max(0, endMs - startMs - accumulatedPausedMs - inFlightPausedMs);
  return Math.round(workingMs / 60000);
}

/**
 * Get on-site minutes for display — pause-aware, with safe fallbacks.
 *
 * Use this everywhere the office or tech UI shows "time on site" / "actual
 * duration" instead of raw `(onsiteEndedAt - onsiteStartedAt)` subtraction.
 * Raw subtraction explodes when a tech pauses on-site and doesn't complete
 * the job until hours/days later — the displayed total swallows the entire
 * paused gap.
 *
 * Priority:
 *   1. job.actualDuration if already stamped (state machine sets this on
 *      WRAPPING_UP / COMPLETED / NEEDS_RETURN_VISIT).
 *   2. An office correction (job-time.service), which is authoritative even
 *      when it is ZERO. Without this, "the tech was never on site, set it to
 *      0" would silently fall through to recomputing from the raw timer and
 *      undo itself. Only jobs carrying onSiteMinutesCorrectedAt take this
 *      path, so no existing job changes behaviour.
 *   3. computeActualDurationMinutes from onsiteEndedAt → completedAt → now.
 *      This subtracts pausedTotalMs + any in-flight pause, so a paused
 *      timer freezes here too.
 *   4. 0 if we don't have enough data.
 */
export function getOnSiteMinutes(job: Job): number {
  if (typeof job.actualDuration === 'number' && job.actualDuration > 0) {
    return job.actualDuration;
  }
  if (job.onSiteMinutesCorrectedAt) {
    return typeof job.actualDuration === 'number' ? job.actualDuration : 0;
  }
  if (!job.onsiteStartedAt) return 0;
  const endIso = job.onsiteEndedAt || job.completedAt || new Date().toISOString();
  return computeActualDurationMinutes(job, endIso);
}

/**
 * Build the Firestore update payload for a state transition.
 * Pure function - does NOT write to Firestore.
 */
export function buildTransitionUpdate(
  job: Job,
  toState: JobState,
  techId: string,
): Record<string, unknown> {
  const now = new Date().toISOString();
  const fromState = resolveJobState(job);
  const signalKey = `${fromState}->${toState}`;
  const lastAction = TRANSITION_SIGNALS[signalKey] || `state_${toState.toLowerCase()}`;

  const update: Record<string, unknown> = {
    jobState: toState,
    lastAction,
    updatedAt: now,
  };

  // State-specific fields
  switch (toState) {
    case 'EN_ROUTE':
      update.enRouteAt = now;
      update.status = 'In Progress';
      break;

    case 'ON_SITE':
      // Calculate travel duration
      if (job.enRouteAt) {
        const travelMs = Date.now() - new Date(job.enRouteAt).getTime();
        update.travelDuration = Math.round(travelMs / 60000);
      }
      break;

    case 'WORKING':
      update.status = 'In Progress';
      // Calculate travel duration if coming from EN_ROUTE (skipping ON_SITE)
      if (job.enRouteAt && fromState === 'EN_ROUTE') {
        const travelMs = Date.now() - new Date(job.enRouteAt).getTime();
        update.travelDuration = Math.round(travelMs / 60000);
      }
      // Start onsite timer if not already started
      if (!job.onsiteStartedAt) {
        update.onsiteStartedAt = now;
        update.startedAt = now;
      }
      // Resume from pause: accumulate the elapsed pause segment and clear pausedAt
      if (fromState === 'PAUSED' && job.pausedAt) {
        update.pausedTotalMs = (job.pausedTotalMs || 0) + (Date.now() - new Date(job.pausedAt).getTime());
        update.pausedAt = null;
      }
      break;

    case 'WRAPPING_UP':
      update.wrapUpStartedAt = now;
      // Stop onsite timer
      if (job.onsiteStartedAt && !job.onsiteEndedAt) {
        update.onsiteEndedAt = now;
        update.actualDuration = computeActualDurationMinutes(job, now);
        // If completing while paused, finalise the pause segment and clear pausedAt
        if (job.pausedAt) {
          update.pausedTotalMs = (job.pausedTotalMs || 0) + (Date.now() - new Date(job.pausedAt).getTime());
          update.pausedAt = null;
        }
      }
      break;

    case 'COMPLETED':
      update.status = 'Completed';
      update.completedAt = now;
      update.completedBy = techId;
      update.lastAction = 'job_completed_summary';
      // Ensure timer is stopped
      if (job.onsiteStartedAt && !job.onsiteEndedAt) {
        update.onsiteEndedAt = now;
        update.actualDuration = computeActualDurationMinutes(job, now);
        if (job.pausedAt) {
          update.pausedTotalMs = (job.pausedTotalMs || 0) + (Date.now() - new Date(job.pausedAt).getTime());
          update.pausedAt = null;
        }
      }
      break;

    case 'NEEDS_RETURN_VISIT':
      update.needsReturnVisit = true;
      update.status = 'Completed'; // The current job is done, but a follow-up is needed
      update.completedAt = now;
      update.completedBy = techId;
      // Timer cleanup
      if (job.onsiteStartedAt && !job.onsiteEndedAt) {
        update.onsiteEndedAt = now;
        update.actualDuration = computeActualDurationMinutes(job, now);
        if (job.pausedAt) {
          update.pausedTotalMs = (job.pausedTotalMs || 0) + (Date.now() - new Date(job.pausedAt).getTime());
          update.pausedAt = null;
        }
      }
      break;

    case 'PAUSED':
      // Freeze the onsite timer: stamp when this pause segment started.
      // pausedTotalMs only accumulates on resume, so a pause-then-complete still
      // carries the in-flight segment forward (handled in COMPLETED/etc above).
      update.pausedAt = now;
      break;

    case 'SCHEDULED':
      // Cancelling en route - clear the en route timestamp
      update.enRouteAt = null;
      update.status = 'Scheduled';
      break;
  }

  return update;
}

/**
 * Perform a state transition.
 * Validates, builds the update, and writes to Firestore.
 * When offline, queues the transition for later replay.
 */
export async function transitionJobState(
  job: Job,
  toState: JobState,
  techId: string,
): Promise<void> {
  const fromState = resolveJobState(job);

  if (!canTransition(fromState, toState)) {
    throw new Error(
      `Invalid transition: ${fromState} → ${toState}`
    );
  }

  if (!job.id) {
    throw new Error('Job must have an ID to transition state');
  }

  const update = buildTransitionUpdate(job, toState, techId);

  // ---- OFFLINE MODE: queue for later sync ----
  if (isOffline()) {
    await enqueueAction({
      type: 'job_state_transition',
      collection: 'jobs',
      docId: job.id,
      data: update,
      description: `${job.client || 'Job'}: ${fromState} → ${toState}`,
      techId,
      meta: {
        jobType: job.type,
        dealId: job.dealId || null,
        faultId: job.faultId || null,
        needsAISummaryBackfill: toState === 'COMPLETED',
      },
    });
    console.log(`[Job State] Offline — queued transition: ${fromState} → ${toState} for job ${job.id}`);
    return; // Don't run fire-and-forget side effects — they'll run on replay
  }

  // ---- ONLINE MODE: write directly, then run side effects via the unified helper ----
  await updateDoc(doc(db, 'jobs', job.id), update);

  // Route all side effects through the shared helper so the online path and
  // offline replay path stay in lockstep. See job-side-effects.service.ts.
  const action: OfflineAction = {
    id: '',
    type: 'job_state_transition',
    collection: 'jobs',
    docId: job.id,
    data: update,
    queuedAt: new Date().toISOString(),
    retryCount: 0,
    description: '',
    techId,
    meta: {
      jobType: job.type,
      dealId: job.dealId || null,
      faultId: job.faultId || null,
      // Online path saves AI summary via saveSummaryToJob BEFORE this call,
      // so no backfill is needed — the helper's re-fetch picks it up.
      needsAISummaryBackfill: false,
    },
  };
  // Fire-and-forget on the online path so the caller (tech UI) returns
  // immediately after the primary updateDoc. The helper itself catches
  // inner failures and records them to `job.lastSideEffectError`, so errors
  // remain observable without blocking the Complete-Job tap.
  // Offline replay path still awaits (see offline-queue.runPostSyncEffects)
  // because dequeue-vs-post-sync ordering depends on it.
  executeJobSideEffects(action).catch((err) => {
    console.error('[transitionJobState] Helper threw unexpectedly:', err);
  });
}

// ============================================================================
// REVERT ("UNDO") LOGIC
// ============================================================================

export interface RevertInfo {
  /** The state the job will move back to */
  toState: JobState;
  /** Short label for the undo affordance, e.g. "Undo 'On my way'" */
  label: string;
}

/**
 * Work out whether the current job state can be stepped back one, and to where.
 * Returns null when there's nothing to undo (already SCHEDULED, terminal, or a
 * legacy state we don't expose an undo for).
 */
export function getRevertInfo(job: Job): RevertInfo | null {
  const state = resolveJobState(job);
  let toState = REVERT_TRANSITIONS[state] ?? null;

  // WORKING undoes to EN_ROUTE only if there was an en-route leg to go back to;
  // otherwise (e.g. a job that jumped straight to WORKING) undo to SCHEDULED.
  if (state === 'WORKING' && !job.enRouteAt) toState = 'SCHEDULED';

  if (!toState) return null;

  const labelMap: Record<string, string> = {
    'EN_ROUTE->SCHEDULED': "Undo 'On my way'",
    'ON_SITE->EN_ROUTE': "Undo 'Arrived'",
    'WORKING->EN_ROUTE': "Undo 'Arrived'",
    'WORKING->SCHEDULED': 'Undo start',
  };

  return { toState, label: labelMap[`${state}->${toState}`] || 'Go back a step' };
}

/**
 * Build the Firestore payload that undoes the current state back one step.
 * Pure function — does NOT write. Clears the timestamps the forward transition
 * set (en-route / onsite timers) so the timers and gates reset cleanly.
 */
export function buildRevertUpdate(job: Job): { toState: JobState; update: Record<string, unknown> } | null {
  const info = getRevertInfo(job);
  if (!info) return null;
  const { toState } = info;
  const now = new Date().toISOString();

  const update: Record<string, unknown> = {
    jobState: toState,
    updatedAt: now,
    lastAction: `state_reverted_to_${toState.toLowerCase()}`,
  };

  if (toState === 'SCHEDULED') {
    // Undo "On my way" (and any onsite leg) — back to a clean scheduled job.
    update.status = 'Scheduled';
    update.enRouteAt = null;
    update.travelDuration = null;
    update.onsiteStartedAt = null;
    update.startedAt = null;
    update.onsiteEndedAt = null;
    update.actualDuration = null;
    update.wrapUpStartedAt = null;
    update.pausedAt = null;
    update.pausedTotalMs = null;
  } else if (toState === 'EN_ROUTE') {
    // Undo "Arrived" — keep the en-route leg (enRouteAt), clear the onsite timer.
    update.status = 'In Progress';
    update.onsiteStartedAt = null;
    update.startedAt = null;
    update.onsiteEndedAt = null;
    update.actualDuration = null;
    update.travelDuration = null; // recomputed on next arrival
    update.wrapUpStartedAt = null;
    update.pausedAt = null;
    update.pausedTotalMs = null;
  }

  return { toState, update };
}

/**
 * Revert ("undo") a job's state back one step. Writes to Firestore (or queues
 * when offline). Returns the new state, or null if there was nothing to undo.
 *
 * Deliberately does NOT fire forward side effects — a revert must never trigger
 * Zapier "arrived"/"completed" events or advance any deal/fault. The only side
 * effect routed through executeJobSideEffects is the deal active-state sync
 * (#2), which is non-terminal-safe and keeps the deal's activeJobState honest.
 */
export async function revertJobState(job: Job, techId: string): Promise<JobState | null> {
  if (!job.id) throw new Error('Job must have an ID to revert state');

  const result = buildRevertUpdate(job);
  if (!result) return null;
  const { toState, update } = result;
  const fromState = resolveJobState(job);

  // ---- OFFLINE MODE: queue for later sync ----
  if (isOffline()) {
    await enqueueAction({
      type: 'job_state_transition',
      collection: 'jobs',
      docId: job.id,
      data: update,
      description: `${job.client || 'Job'}: undo ${fromState} → ${toState}`,
      techId,
      meta: {
        jobType: job.type,
        dealId: job.dealId || null,
        faultId: job.faultId || null,
        needsAISummaryBackfill: false,
      },
    });
    console.log(`[Job State] Offline — queued revert: ${fromState} → ${toState} for job ${job.id}`);
    return toState;
  }

  // ---- ONLINE MODE: write directly, then sync deal active-state only ----
  await updateDoc(doc(db, 'jobs', job.id), update);

  const action: OfflineAction = {
    id: '',
    type: 'job_state_transition',
    collection: 'jobs',
    docId: job.id,
    data: update,
    queuedAt: new Date().toISOString(),
    retryCount: 0,
    description: '',
    techId,
    meta: {
      jobType: job.type,
      dealId: job.dealId || null,
      faultId: job.faultId || null,
      needsAISummaryBackfill: false,
    },
  };
  // Fire-and-forget. toState is non-terminal, so the helper only runs the deal
  // active-state sync and bails before any forward/terminal effects.
  executeJobSideEffects(action).catch((err) => {
    console.error('[revertJobState] Helper threw unexpectedly:', err);
  });

  return toState;
}

/**
 * Complete a job with return visit info.
 * Transitions to NEEDS_RETURN_VISIT and sets the reason/notes.
 */
export async function completeWithReturnVisit(
  job: Job,
  techId: string,
  reason: string,
  notes?: string,
  createFollowUpJob = true,
): Promise<void> {
  if (!job.id) throw new Error('Job must have an ID');

  const fromState = resolveJobState(job);
  if (!canTransition(fromState, 'NEEDS_RETURN_VISIT')) {
    throw new Error(`Cannot request return visit from ${fromState} state`);
  }

  const update = buildTransitionUpdate(job, 'NEEDS_RETURN_VISIT', techId);
  update.returnVisitReason = reason;
  if (notes) update.returnVisitNotes = notes;
  update.returnVisitContext = {
    originalJobId: job.id,
    originalCompletedAt: update.completedAt,
    originalTechId: techId,
    reason,
    techNotes: notes || '',
  };
  // Track whether a follow-up job was auto-created or is blocked
  update.returnVisitStatus = createFollowUpJob ? 'ready_to_schedule' : 'blocked';

  // ---- OFFLINE MODE: queue for later sync ----
  if (isOffline()) {
    await enqueueAction({
      type: 'return_visit',
      collection: 'jobs',
      docId: job.id!,
      data: update,
      description: `${job.client || 'Job'}: Return visit — ${reason}`,
      techId,
      meta: {
        jobType: job.type,
        dealId: job.dealId || null,
        faultId: job.faultId || null,
        reason,
        notes: notes || '',
        createFollowUpJob,
        // Match transitionJobState's offline path: if no AI summary was saved
        // before going offline, the replay should backfill one. Fixes M14
        // inconsistency noted in the SP8 post-merge audit.
        needsAISummaryBackfill: true,
      },
    });
    console.log(`[Job State] Offline — queued return visit for job ${job.id}`);
    return; // Follow-up job creation happens on replay
  }

  await updateDoc(doc(db, 'jobs', job.id), update);

  // Route all side effects (fault sync, follow-up job creation, deal sync,
  // deal active-state clear) through the unified helper. See
  // job-side-effects.service.ts for the full inventory.
  const action: OfflineAction = {
    id: '',
    type: 'return_visit',
    collection: 'jobs',
    docId: job.id,
    data: update,
    queuedAt: new Date().toISOString(),
    retryCount: 0,
    description: '',
    techId,
    meta: {
      jobType: job.type,
      dealId: job.dealId || null,
      faultId: job.faultId || null,
      reason,
      notes: notes || '',
      createFollowUpJob,
    },
  };
  // Fire-and-forget — errors are captured by the helper and written to
  // `job.lastSideEffectError`. See transitionJobState for rationale.
  executeJobSideEffects(action).catch((err) => {
    console.error('[completeWithReturnVisit] Helper threw unexpectedly:', err);
  });
}

/**
 * Office-side "force complete" override.
 *
 * Marks a job Completed from the office (SchedulePage detail panel) when a
 * tech's completion never reached the server — e.g. the tech's device showed
 * the job done but a failed / never-synced write left it sitting open in the
 * schedule. This is the manual recovery path for that mismatch.
 *
 * Unlike transitionJobState this deliberately does NOT gate on canTransition:
 * a stuck job can be in any pre-terminal state (Scheduled / In Progress), and
 * the office needs to be able to close it regardless of where it's wedged. It
 * writes the same completed fields a real completion writes (via
 * buildTransitionUpdate) plus an audit marker recording who forced it and why,
 * then fires the standard side-effect chain (fault → completed, deal → review,
 * service activation) so the downstream sync the tech's failed write skipped
 * still runs.
 *
 * Always writes directly (no offline queue) — the office is online when they
 * use this. Awaits the side effects (unlike the fire-and-forget tech path) so
 * the caller can surface any downstream sync warnings. executeJobSideEffects
 * never throws; it returns its errors in the SideEffectResult.
 */
export async function forceCompleteJobFromOffice(
  job: Job,
  officeUserId: string,
  officeUserName?: string,
  reason?: string,
): Promise<SideEffectResult> {
  if (!job.id) throw new Error('Job must have an ID to force-complete');

  const now = new Date().toISOString();
  const update = buildTransitionUpdate(job, 'COMPLETED', officeUserId);
  // Distinct signal so downstream automations / audits can tell an office
  // override apart from a genuine tech completion — we don't masquerade as the
  // tech's 'job_completed_summary'. Side effects key off jobState, not
  // lastAction, so the fault/deal/service chain still fires.
  update.lastAction = 'office_force_completed';
  update.forceCompletedBy = officeUserId;
  update.forceCompletedAt = now;
  if (officeUserName) update.forceCompletedByName = officeUserName;
  if (reason && reason.trim()) update.forceCompleteReason = reason.trim();

  await updateDoc(doc(db, 'jobs', job.id), update);

  const action: OfflineAction = {
    id: '',
    type: 'job_state_transition',
    collection: 'jobs',
    docId: job.id,
    data: update,
    queuedAt: now,
    retryCount: 0,
    description: '',
    techId: officeUserId,
    meta: {
      jobType: job.type,
      dealId: job.dealId || null,
      faultId: job.faultId || null,
      // No AI-summary backfill — an office override shouldn't fabricate the
      // tech's reasoning/summary. Summaries stay on the real completion path.
      needsAISummaryBackfill: false,
    },
  };
  return executeJobSideEffects(action);
}

/**
 * Check if job completion is allowed.
 * All required checkpoints must be completed.
 */
export function canCompleteJob(job: Job): boolean {
  if (!job.checkpoints || job.checkpoints.length === 0) return true;
  return job.checkpoints.filter((cp) => cp.required).every((cp) => cp.completed);
}

/**
 * Check if JSA is required but not completed.
 * Currently disabled — JSA is on backburner.
 * Arrival now goes straight to WORKING state, skipping ON_SITE.
 */
export function isJSARequired(_job: Job): boolean {
  return false;
}
